import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { AuthUser, SessionInfo } from "../shared/api-types";
import * as authApi from "../api/auth";

interface AuthContextValue {
  user: AuthUser | null;
  csrfToken: string | null;
  sessions: SessionInfo[];
  status: "loading" | "ready" | "error";
  error: string | null;
  refresh: () => Promise<void>;
  signIn: (input: authApi.Credentials) => Promise<void>;
  signUp: (input: authApi.Credentials & { displayName: string }) => Promise<string>;
  recoverAccount: (input: { email: string; recoveryCode: string; newPassword: string }) => Promise<string>;
  signOut: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  removeAccount: (password: string) => Promise<void>;
  invalidate: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [csrfToken, setCsrfToken] = useState<string | null>(null);
  const [sessions, setSessions] = useState<SessionInfo[]>([]);
  const [status, setStatus] = useState<AuthContextValue["status"]>("loading");
  const [error, setError] = useState<string | null>(null);
  const authEpoch = useRef(0);

  const applyMe = useCallback((me: Awaited<ReturnType<typeof authApi.getMe>>) => {
    setUser(me.user);
    setCsrfToken(me.csrfToken);
    setSessions(me.sessions);
    setError(null);
    setStatus("ready");
  }, []);

  const applySession = useCallback((nextUser: AuthUser, nextCsrfToken: string) => {
    authEpoch.current += 1;
    setUser(nextUser);
    setCsrfToken(nextCsrfToken);
    setSessions([]);
    setError(null);
    setStatus("ready");
  }, []);

  const refreshDetails = useCallback(() => {
    const epoch = authEpoch.current;
    void authApi.getMe().then((me) => {
      if (epoch === authEpoch.current) applyMe(me);
    }).catch(() => undefined);
  }, [applyMe]);

  const invalidate = useCallback(() => {
    authEpoch.current += 1;
    setUser(null);
    setCsrfToken(null);
    setSessions([]);
    setError(null);
    setStatus("ready");
  }, []);

  const refresh = useCallback(async () => {
    const epoch = ++authEpoch.current;
    setStatus("loading");
    try {
      const me = await authApi.getMe();
      if (epoch === authEpoch.current) applyMe(me);
    } catch (cause) {
      if (epoch !== authEpoch.current) return;
      setError(cause instanceof Error ? cause.message : "无法验证登录状态。");
      setStatus("error");
    }
  }, [applyMe]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signIn = useCallback(async (input: authApi.Credentials) => {
    const result = await authApi.login(input);
    applySession(result.user, result.csrfToken);
    refreshDetails();
  }, [applySession, refreshDetails]);

  const signUp = useCallback(async (input: authApi.Credentials & { displayName: string }) => {
    const result = await authApi.register(input);
    applySession(result.user, result.csrfToken);
    refreshDetails();
    return result.recoveryCode;
  }, [applySession, refreshDetails]);

  const recoverAccount = useCallback(async (input: {
    email: string;
    recoveryCode: string;
    newPassword: string;
  }) => {
    const result = await authApi.recover(input);
    applySession(result.user, result.csrfToken);
    refreshDetails();
    return result.recoveryCode;
  }, [applySession, refreshDetails]);

  const signOut = useCallback(async () => {
    if (csrfToken) await authApi.logout(csrfToken);
    invalidate();
  }, [csrfToken, invalidate]);

  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    if (!csrfToken || !user) throw new Error("会话已失效，请重新登录。");
    await authApi.changePassword(user.email, currentPassword, newPassword, csrfToken);
    setSessions((current) => current.filter((session) => session.current));
  }, [csrfToken, user]);

  const removeAccount = useCallback(async (password: string) => {
    if (!csrfToken || !user) throw new Error("会话已失效，请重新登录。");
    await authApi.deleteAccount(user.email, password, csrfToken);
    invalidate();
  }, [csrfToken, invalidate, user]);

  const value = useMemo<AuthContextValue>(() => ({
    user,
    csrfToken,
    sessions,
    status,
    error,
    refresh,
    signIn,
    signUp,
    recoverAccount,
    signOut,
    changePassword,
    removeAccount,
    invalidate,
  }), [
    user, csrfToken, sessions, status, error, refresh, signIn, signUp,
    recoverAccount, signOut, changePassword, removeAccount, invalidate,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}
